import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { DestroyBaseComponent } from '@components/abstract/DestroyBaseComponent';
import { AltLabel } from '@shared/constants/alt-label';
import { ApplicationInsightsService } from '@core/services/application-insights.service';
import { LoggerService } from '@core/services/logger.service';
import { UserService } from '@core/services/user.service';
import { filter, switchMap, take, takeUntil } from 'rxjs';

const MSAL_FAILURE_EVENT_TYPES: EventType[] = [
  EventType.LOGIN_FAILURE,
  EventType.ACQUIRE_TOKEN_FAILURE,
  EventType.SSO_SILENT_FAILURE
];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends DestroyBaseComponent implements OnInit {
  apiResponse: string | undefined;
  showOptions: boolean = false;
  public isUserAdmin: boolean = false;
  isLoginInProgress = false;
  logoAlt = AltLabel.Logo;
  footerLogoAlt = AltLabel.FooterLogo;

  myMatchOptions: IsActiveMatchOptions = {
    queryParams: 'ignored',
    matrixParams: 'exact',
    paths: 'subset',
    fragment: 'exact'
  };
  constructor(
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly elementRef: ElementRef,
    private readonly userService: UserService,
    private readonly msalBroadcastService: MsalBroadcastService,
    readonly appInsights: ApplicationInsightsService,
    private readonly logger: LoggerService
  ) {
    super();
  }

  @HostListener('document:click', ['$event.target'])
  public onPageClick(targetElement: HTMLElement) {
    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside || !targetElement.hasAttribute('burger-menu')) {
      this.showOptions = false;
    }
  }

  ngOnInit(): void {
    this.setupLoginProcessListeners();
    this.assignUserDetail();
    this.setIsUserAdmin();
    this.setupMsalFailureLogging();
  }

  get isUserInAnyGroup() {
    return this.userService.isUserInAnyGroup;
  }

  private setupLoginProcessListeners(): void {
    this.listenForLoginStatusChange(InteractionStatus.HandleRedirect, () => (this.isLoginInProgress = true));
    this.listenForLoginStatusChange(InteractionStatus.None, () => (this.isLoginInProgress = false));
  }

  private assignUserDetail(): void {
    this.msalService
      .initialize()
      .pipe(
        switchMap(() => this.msalService.handleRedirectObservable()),
        filter(response => !!response?.account),
        takeUntil(this.destroy$)
      )
      .subscribe(response => {
        this.msalService.instance.setActiveAccount(response.account);
        this.appInsights.setUser(response.account!.localAccountId, response.account!.homeAccountId);
      });
  }

  /**
   * Check if the login is still in progress.
   * When a sign in button is clicked, the spinner should still show until the app sets the user detail
   * @param status Interaction status to listen for; startUp, handleRedirect and None
   * @param callback Callback function
   */
  private listenForLoginStatusChange(status: InteractionStatus, callback: () => void): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((currentStatus: InteractionStatus) => currentStatus === status),
        takeUntil(this.destroy$)
      )
      .subscribe(() => callback());
  }

  /**
   * msalSubject$ was previously unused (only inProgress$, purely to drive the login spinner) — a repeated
   * forced re-login had no trace on the frontend explaining why, matching the backend Entra ID logging
   * work already done for this report.
   */
  private setupMsalFailureLogging(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((message: EventMessage) => MSAL_FAILURE_EVENT_TYPES.includes(message.eventType)),
        takeUntil(this.destroy$)
      )
      .subscribe((message: EventMessage) => {
        this.logger.warn(message.eventType, { error: String(message.error ?? '') });
      });
  }

  private setIsUserAdmin(): void {
    this.userService
      .isUserAdmin()
      .pipe(take(1))
      .subscribe(a => {
        this.isUserAdmin = a;
      });
  }

  toggleOptions() {
    this.showOptions = !this.showOptions;
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getActiveAccount() != null;
  }

  userExists(): boolean {
    return this.msalService.instance.getActiveAccount() != null;
  }

  logout() {
    //the router.navigate below is a workaround for the issue where
    //msalService.logoutRedirect() itself does not trigger pendingChangeGuard()
    this.appInsights.clearUser();
    const url = this.router.url;
    this.router.navigate(['/']).then(() => {
      if (url === '/' || url !== this.router.url) {
        this.msalService.logoutRedirect();
      }
    });
  }

  onClickBulkUpdate() {
    this.showOptions = !this.showOptions;
    this.router.navigate(['bulk-update']);
  }

  onAdministrationMenuClicked() {
    this.showOptions = false;
    this.router.navigate(['administration', 'edit-lookups']);
  }

  getActiveAccountName() {
    return this.msalService.instance.getActiveAccount()?.name;
  }
}
